import { createClient, type User, type Session } from "@supabase/supabase-js"
import { toLocalISODate } from "@/lib/utils"
import type { BeginnerExperience, StrengthMovement, TargetZone } from "@/lib/strengthStandards"
import { programStartResetFor } from "@/types/programs"
import {
  parseAuthRedirect,
  isFromApp,
  savePending,
  resetRedirectPath,
  resetRequestErrorMessage,
  classifyUpdatePasswordError,
  type AuthRedirect,
  type UpdatePasswordError,
} from "@/lib/passwordRecovery"

const supabaseUrl = "https://hzuvklijyvnpginpeiwy.supabase.co"
const supabaseAnonKey =
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imh6dXZrbGlqeXZucGdpbnBlaXd5Iiwicm9sZSI6ImFub24iLCJpYXQiOjE3Njc2NTA4OTMsImV4cCI6MjA4MzIyNjg5M30.4jvRcTjJiMT3Q0EgOaWSJl7Br8BLkWel8wazfCtZqXs"

const noopLock = async <R>(
  _name: string,
  _acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> => {
  return await fn()
}

/**
 * Adresse de retour d'un lien d'authentification, lue AVANT `createClient` : la librairie
 * efface le `#access_token=…&type=recovery` dès qu'elle a ouvert la session de
 * récupération, et l'écran « nouveau mot de passe » n'aurait plus rien pour se décider.
 * Voir `passwordRecovery.ts`.
 */
export const initialAuthRedirect: AuthRedirect = parseAuthRedirect(window.location.search, window.location.hash)
if (initialAuthRedirect.kind === "recovery") {
  savePending({ userId: initialAuthRedirect.userId, fromApp: isFromApp(window.location.search) })
}

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
    // Valeur par défaut, écrite pour qu'elle ne change pas par mégarde : en `pkce`, le
    // lien « mot de passe oublié » ne marcherait que dans le navigateur qui l'a demandé
    // (le secret de l'échange y est stocké). Or sur iPhone, un lien demandé depuis l'app
    // installée s'ouvre dans le navigateur, qui n'a pas ce stockage.
    flowType: "implicit",
    storage: window.localStorage,
    lock: noopLock,
  },
})

// ============================================
// TYPES
// ============================================

export type { User, Session } from "@supabase/supabase-js"

export type WizardSubmission = {
  user_id?: string
  first_name: string
  /** Email du COMPTE (auth.users), recopié — plus jamais saisi à la main. */
  email: string
  gender: "male" | "female"
  age: number
  height: number
  weight: number
  place: "gym" | "home"
  level_id: string
  goal_id: string
  /** Obsolète depuis le lot 3 : le diagnostic ne l'écrit plus (formule = subscriptions.plan). */
  offer_id?: string | null
  birth_date?: string | null
  weight_start?: number | null
  program_week_start?: string | null
  avatar_url?: string | null
  beginner_experience?: BeginnerExperience | null
  training_days?: string[]
  target_weight?: number | null
  phone?: string | null
}

export type WizardSubmissionRow = {
  id: string
  created_at: string
  user_id: string
  email: string
  first_name: string
  gender: "male" | "female"
  age: number
  height: number
  weight: number
  place: "gym" | "home"
  level_id: string
  goal_id: string
  /** NULL pour les comptes créés depuis le lot 3. */
  offer_id: string | null
  birth_date: string | null
  weight_start: number | null
  program_week_start: string | null
  /**
   * Première séance du bloc en cours : 1 par défaut, 5 après un changement de niveau
   * (semaine d'adaptation sautée). Écrite par `check-and-populate-next-block`, remise à 1
   * par `programStartResetFor` quand le programme change à la main.
   */
  program_start_session: number
  avatar_url: string | null
  beginner_experience: BeginnerExperience | null
  /** Codes ISO courts ('mon'..'sun'). Tableau vide = pas de préférence de jours. */
  training_days: string[]
  target_weight: number | null
  phone: string | null
  /** Accord explicite pour les blessures (heure du serveur). NULL = pas d'accord. */
  injury_consent_at: string | null
  injury_consent_version: string | null
  /**
   * `programme` (défaut) : séances Zeyone planifiées. `suivi_seul` : client qui a déjà
   * un coach — aucune séance Zeyone (bloqué en base), il déclare ses séances libres.
   * Posé à la main (scripts/sql/suivi-seul-activer-compte.sql), jamais par l'app.
   */
  tracking_mode: TrackingMode
}

export type TrackingMode = "programme" | "suivi_seul"

/** Compte en suivi seul. Une ligne lue avant la migration (champ absent) reste en programme. */
export const isSuiviSeul = (wizard: Pick<WizardSubmissionRow, "tracking_mode"> | null | undefined): boolean =>
  wizard?.tracking_mode === "suivi_seul"

export type CalendarEventType =
  | "seance-planned" | "seance-done" | "seance-libre" | "activite" | "recuperation"

export type CalendarEventRow = {
  id: string
  user_id: string
  type: CalendarEventType
  title: string
  event_date: string
  end_time: string | null
  session_ref: string | null
  meta: Record<string, unknown>
  programme_session_number?: number | null
  /** Contenu d'une séance libre (texte libre, facultatif). */
  content?: string | null
  created_at: string
  updated_at: string
}

export type CalendarEventInput = {
  type: CalendarEventType
  title: string
  event_date: string
  end_time?: string | null
  session_ref?: string | null
  meta?: Record<string, unknown>
  content?: string | null
}

// ── Daily Wellness (Hooper) ──
export type DailyWellnessInput = {
  date: string
  sleep_quality: number
  muscle_freshness: number
  mood: number
  soreness: number
  pain_present: boolean
  pain_location: string | null
  pain_intensity: number | null
}

export type DailyWellnessRow = {
  id: string
  user_id: string
  email: string | null
  date: string
  sleep_quality: number
  muscle_freshness: number
  mood: number
  soreness: number
  hooper_score: number
  hooper_status: string | null
  pain_present: boolean
  pain_location: string | null
  pain_intensity: number | null
  created_at: string
}

// ── Training Load (Post-Session) ──
export type PostSessionInput = {
  date: string
  session_duration: number
  rpe: number
  pleasure: number
  training_load: number
  programme_id?: string | null
  programme_session_number?: number | null
}

export type TrainingLoadRow = {
  id: string
  user_id: string
  email: string | null
  date: string
  session_duration: number
  rpe: number
  training_load: number
  acute_load_ewma: number | null
  chronic_load_ewma: number | null
  acwr: number | null
  acwr_zone: string | null
  created_at: string
}

// ── Session Feedback ──
export interface SessionMetaExercise {
  name: string
  sets: string
  reps: string
  weight: string
}

export interface SessionMeta {
  displayName: string
  method: string | null
  estimatedDuration: string
  exerciseCount: number
  totalSets: number
  exerciseNames: string[]
  exercises?: SessionMetaExercise[]
  end_time?: string
}

// ── Session Set Logs (exécution réelle, série par série) ──
// charge_basis reprend la classification de exerciseNormalization.ts (ChargeBasis) —
// dupliqué ici en littéral plutôt qu'importé pour ne pas coupler lib/supabase.ts
// au module de calcul de charge.
export type SessionSetChargeBasis =
  | "external"
  | "bodyweight_net"
  | "bodyweight_gross"
  | "estimated_range"
  | "not_applicable"

export interface SessionSetLogInput {
  programme_id: string
  programme_session_number: number
  round: number
  exercise_index: number
  movement_index: number
  exercise_name: string
  reps: string
  tempo: string | null
  charge_kg: number | null
  charge_basis: SessionSetChargeBasis
  per_dumbbell: boolean
  sets_count: number
  completed_at: string
}

export type SessionSetLogRow = SessionSetLogInput & {
  id: string
  user_id: string
  created_at: string
  // Enrichis côté lecture par getProgrammeSetLogs (jointure manuelle vers
  // program_sessions — pas de FK entre les deux tables, donc pas de colonnes
  // réelles ici). is_deload : la séance de programme correspondante est une
  // décharge planifiée. session_type : "Push", "Full Body", "Testing 1RM"…,
  // unique au sein d'une semaine de programme (vérifié sur tout le catalogue).
  // programme_week : program_sessions.week. false/null si program_sessions ne
  // renvoie rien pour ce (programme_id, programme_session_number) — ex. séance
  // test/factice.
  is_deload: boolean
  session_type: string | null
  programme_week: number | null
}

export type SessionFeedbackRow = {
  id: string
  user_id: string
  email: string | null
  session_id: string | null
  programme_id: string | null
  programme_session_number: number | null
  completed_at: string
  actual_duration_minutes: number | null
  rpe: number | null
  enjoyment: number | null
  fatigue: number | null
  notes: string | null
  session_meta: SessionMeta | null
  created_at: string
}

// ── Athlete Profile ──
export type AthleteProfileRow = {
  id: string
  user_id: string
  email: string | null
  rm_bench_press: number | null
  rm_pull_up: number | null
  rm_back_squat: number | null
  rm_deadlift: number | null
  rm_overhead_press: number | null
  rm_test_date: string | null
  coach_notes: string | null
  injuries: string | null
  medical_notes: string | null
  created_at: string
  updated_at: string
}

export type AthleteProfileInput = {
  rm_bench_press?: number | null
  rm_pull_up?: number | null
  rm_back_squat?: number | null
  rm_deadlift?: number | null
  rm_overhead_press?: number | null
  rm_test_date?: string | null
  coach_notes?: string | null
  injuries?: string | null
  medical_notes?: string | null
}

// ── Weight History ──
export type WeightHistoryRow = {
  id: string
  user_id: string
  date: string
  weight: number
  note: string | null
  created_at: string
}

// ── Body Measurements (Lot 2a — Profil) ──
export type BodyFatSource = "impedance" | "dexa" | "coach" | "auto"

export const BODY_FAT_SOURCE_LABELS: Record<BodyFatSource, string> = {
  impedance: "Balance à impédancemétrie",
  dexa: "DEXA",
  coach: "Estimation coach",
  auto: "Estimation automatique",
}

export type BodyMeasurementsRow = {
  id: string
  user_id: string
  date: string
  waist_cm: number | null
  neck_cm: number | null
  hips_cm: number | null
  shoulders_cm: number | null
  chest_cm: number | null
  arm_right_cm: number | null
  arm_left_cm: number | null
  forearm_cm: number | null
  thigh_cm: number | null
  calf_cm: number | null
  body_fat_pct_manual: number | null
  body_fat_source: BodyFatSource | null
  note: string | null
  created_at: string
}

export type BodyMeasurementsInput = Partial<
  Pick<
    BodyMeasurementsRow,
    | "waist_cm" | "neck_cm" | "hips_cm" | "shoulders_cm" | "chest_cm"
    | "arm_right_cm" | "arm_left_cm" | "forearm_cm" | "thigh_cm" | "calf_cm"
    | "body_fat_pct_manual" | "body_fat_source" | "note"
  >
>

// ── Notifications (Session 5) ──
export type NotificationType =
  | "post-session"
  | "hooper"
  /**
   * « Ton bilan de la semaine » : plus écrit depuis le 16/10 (schedule-weekly-push
   * supprimé, le bilan est à jour en continu dans l'app). Gardé tant que la cloche
   * contient d'anciennes entrées de ce type.
   */
  | "weekly"
  | "reminder"
  | "achievement"
  | "weight"
  /** « Tes notifications sont coupées » : écrite par send-push quand le dernier appareil décroche. */
  | "push"

export type NotificationRow = {
  id: string
  user_id: string
  type: NotificationType
  title: string
  body: string
  action_url: string | null
  read: boolean
  created_at: string
}

export type NotificationInput = {
  type: NotificationType
  title: string
  body: string
  action_url?: string | null
}

// ============================================
// AUTH
// ============================================

export type AuthResult = {
  user: User | null
  session: Session | null
  error: string | null
}

export async function signUp(email: string, password: string, firstName?: string): Promise<AuthResult> {
  const { data, error } = await supabase.auth.signUp({
    email: email.trim().toLowerCase(),
    password,
    options: {
      data: firstName ? { first_name: firstName.trim() } : undefined,
      // Sans lui, le lien de confirmation part vers la Site URL de Supabase. La
      // confirmation est désactivée aujourd'hui (Q14) : prêt pour le jour où elle
      // sera activée, et correct quel que soit le domaine (app., localhost).
      emailRedirectTo: `${window.location.origin}/auth`,
    },
  })
  if (error) return { user: null, session: null, error: translateAuthError(error.message) }
  return { user: data.user, session: data.session, error: null }
}

export async function signIn(email: string, password: string): Promise<AuthResult> {
  const doSignIn = async () =>
    await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password })

  try {
    const { data, error } = await doSignIn()
    if (error) {
      if (error.message.toLowerCase().includes("invalid login credentials")) {
        return { user: null, session: null, error: translateAuthError(error.message) }
      }
      console.warn("[signIn] retry dans 500ms:", error.message)
      await new Promise((r) => setTimeout(r, 500))
      const { data: data2, error: error2 } = await doSignIn()
      if (error2) return { user: null, session: null, error: translateAuthError(error2.message) }
      return { user: data2.user, session: data2.session, error: null }
    }
    return { user: data.user, session: data.session, error: null }
  } catch (err) {
    return { user: null, session: null, error: err instanceof Error ? err.message : "Erreur inconnue" }
  }
}

/**
 * `global` (défaut de Supabase) ferme la session sur TOUS les appareils du compte ;
 * `local`, sur celui-ci seulement — pour quitter un compte qui n'est pas le sien
 * (écran « Ce bilan est pour un autre compte ») sans déconnecter son propriétaire
 * partout ailleurs.
 */
export async function signOut(scope: "global" | "local" = "global"): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.signOut({ scope })
  return { error: error ? translateAuthError(error.message) : null }
}

/**
 * Envoie le lien « mot de passe oublié ». `fromApp` : la demande part de l'app installée
 * (ou d'un lien qui en venait) — l'écran de fin dira alors au client d'y revenir.
 * Supabase ne dit pas si l'adresse a un compte : pas d'erreur pour une adresse inconnue.
 */
export async function resetPassword(email: string, fromApp = false): Promise<{ error: string | null }> {
  try {
    const { error } = await supabase.auth.resetPasswordForEmail(
      email.trim().toLowerCase(),
      { redirectTo: `${window.location.origin}${resetRedirectPath(fromApp)}` }
    )
    if (error) {
      // Le code exact (email_address_invalid, over_email_send_rate_limit…) ne s'affiche pas
      // au client : on le garde pour le diagnostic.
      console.error("[resetPassword]", error.code, error.status, error.message)
      return { error: resetRequestErrorMessage(error) }
    }
    return { error: null }
  } catch (err) {
    return { error: resetRequestErrorMessage({ message: err instanceof Error ? err.message : "" }) }
  }
}

/**
 * Enregistre le nouveau mot de passe (session de récupération ouverte par le lien), puis
 * déconnecte les AUTRES appareils (décision du 05/10) : si l'ancien mot de passe a fuité,
 * l'intrus perd l'accès. Cet appareil-ci reste connecté. Un échec de cette déconnexion
 * n'annule pas le changement : il est journalisé, sans plus.
 */
export async function updatePassword(password: string): Promise<{ error: UpdatePasswordError | null }> {
  try {
    const { error } = await supabase.auth.updateUser({ password })
    if (error) {
      console.error("[updatePassword]", error.code, error.message)
      return { error: classifyUpdatePasswordError(error) }
    }
  } catch (err) {
    console.error("[updatePassword] exception:", err)
    return { error: "generic" }
  }
  try {
    const { error } = await supabase.auth.signOut({ scope: "others" })
    if (error) console.error("[updatePassword] signOut others:", error.message)
  } catch (err) {
    console.error("[updatePassword] signOut others exception:", err)
  }
  return { error: null }
}

export async function getCurrentSession(): Promise<Session | null> {
  const { data } = await supabase.auth.getSession()
  return data.session
}

export async function getCurrentUser(): Promise<User | null> {
  const { data } = await supabase.auth.getUser()
  return data.user
}

function translateAuthError(msg: string): string {
  const m = msg.toLowerCase()
  if (m.includes("invalid login credentials")) return "Email ou mot de passe incorrect."
  if (m.includes("email not confirmed")) return "Email non confirmé."
  if (m.includes("user already registered")) return "Un compte existe déjà avec cet email."
  if (m.includes("password should be at least")) return "Mot de passe ≥ 6 caractères."
  if (m.includes("rate limit")) return "Trop de tentatives. Réessaie plus tard."
  if (m.includes("network")) return "Problème de connexion réseau."
  if (m.includes("lockmanager")) return "Conflit de session. Rafraîchis."
  return msg
}

// ============================================
// HELPERS
// ============================================

async function requireUser(): Promise<{ id: string; email: string | null }> {
  const { data: userData } = await supabase.auth.getUser()
  const userId = userData.user?.id
  if (!userId) throw new Error("Utilisateur non authentifié")
  return { id: userId, email: userData.user?.email ?? null }
}

async function requireUserId(): Promise<string> {
  const { id } = await requireUser()
  return id
}

// ============================================
// WIZARD
// ============================================

export const saveWizardSubmission = async (data: WizardSubmission) => {
  let userId = data.user_id
  if (!userId) { userId = await requireUserId(); data.user_id = userId }

  if (data.weight && !data.weight_start) data.weight_start = data.weight
  if (!data.program_week_start) data.program_week_start = new Date().toISOString().split("T")[0]

  const { data: existing, error: checkError } = await supabase
    .from("wizard_submissions").select("id, weight_start, program_week_start, level_id, goal_id, place, gender")
    .eq("user_id", userId).maybeSingle()
  if (checkError) throw new Error(`Lecture impossible : ${checkError.message}`)

  if (existing) {
    const { weight_start: _ws, program_week_start: _pws, ...rest } = data
    const { data: result, error } = await supabase
      .from("wizard_submissions").update({ ...rest, ...programStartResetFor(existing, rest) })
      .eq("user_id", userId).select().single()
    if (error) throw new Error(`Mise à jour échouée : ${error.message}`)
    return result as WizardSubmissionRow
  }

  // Plus de repli « 23505 → mise à jour par email » : l'unicité porte sur user_id, pas
  // sur l'email (audit §0.2). Ce repli mettait à jour par email après un conflit de
  // user_id — sans objet depuis que la lecture ci-dessus trouve la ligne existante.
  const { data: result, error } = await supabase
    .from("wizard_submissions").insert([data]).select().single()
  if (error) throw new Error(`Sauvegarde échouée : ${error.message}`)
  return result as WizardSubmissionRow
}

export const getWizardSubmissionForUser = async (userId: string): Promise<WizardSubmissionRow | null> => {
  const { data, error } = await supabase
    .from("wizard_submissions").select("*").eq("user_id", userId)
    .order("created_at", { ascending: false }).limit(1).maybeSingle()
  if (error) { console.error("Erreur lecture wizard:", error); return null }
  return data as WizardSubmissionRow | null
}

// ============================================
// CALENDAR EVENTS
// ============================================

export const listCalendarEvents = async (startDate: string, endDate: string): Promise<CalendarEventRow[]> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("calendar_events").select("*").eq("user_id", userId)
    .gte("event_date", startDate).lte("event_date", endDate)
    .order("event_date", { ascending: true })
  if (error) throw new Error(`Lecture calendrier impossible : ${error.message}`)
  return (data ?? []) as CalendarEventRow[]
}

export const createCalendarEvent = async (input: CalendarEventInput): Promise<CalendarEventRow> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("calendar_events").insert([{ ...input, user_id: userId }]).select().single()
  if (error) throw new Error(`Création impossible : ${error.message}`)
  return data as CalendarEventRow
}

export const updateCalendarEvent = async (id: string, updates: Partial<CalendarEventInput>): Promise<CalendarEventRow> => {
  const { data, error } = await supabase
    .from("calendar_events").update(updates).eq("id", id).select().single()
  if (error) throw new Error(`Mise à jour impossible : ${error.message}`)
  return data as CalendarEventRow
}

export const deleteCalendarEvent = async (id: string): Promise<void> => {
  const { error } = await supabase.from("calendar_events").delete().eq("id", id)
  if (error) throw new Error(`Suppression impossible : ${error.message}`)
}

// ── Questionnaire post-séance des activités ajoutées au calendrier ──
// (séance libre, activité sportive, récupération active). La réponse vit dans
// `calendar_events.meta.post_session` ; la charge s'ajoute à `training_load` sur le
// jour de l'activité. Push à fin + 30 min : déclencheur en base (migration 20261013120000).

export const ACTIVITY_EVENT_TYPES: CalendarEventType[] = ["seance-libre", "activite", "recuperation"]

export type ActivityPostSessionAnswer =
  | { status: "done"; duration: number; rpe: number; pleasure: number; training_load: number; answered_at: string }
  | { status: "skipped"; answered_at: string }

export const getActivityPostSessionAnswer = (event: Pick<CalendarEventRow, "meta">): ActivityPostSessionAnswer | null =>
  ((event.meta ?? {}) as { post_session?: ActivityPostSessionAnswer }).post_session ?? null

/** Fin prévue de l'activité (heure locale de l'appareil), null sans heure de fin. */
export const getActivityEndAt = (event: Pick<CalendarEventRow, "event_date" | "end_time">): Date | null => {
  if (!event.end_time) return null
  const [y, m, d] = event.event_date.split("-").map(Number)
  const [h, min] = event.end_time.split(":").map(Number)
  return new Date(y, m - 1, d, h, min)
}

/**
 * Ce qui attend une réponse au questionnaire post-séance, dans l'ordre où le bouton
 * « Post-séance » du Monitoring l'ouvre (questionnaire sans `?event=`) et où son
 * indicateur l'annonce :
 *   1. la dernière séance du programme terminée sans questionnaire (ligne partielle,
 *      rpe = null) ;
 *   2. parmi les activités du calendrier sans réponse des 7 derniers jours, la
 *      dernière terminée ;
 *   3. à défaut, la prochaine fin prévue d'aujourd'hui → `locked` (questionnaire
 *      « disponible à HHhMM »).
 * null : rien à remplir.
 */
export type PostSessionPending =
  | { kind: "program"; programme_id: string; session_number: number }
  | { kind: "activity"; eventId: string; endAt: Date; locked: boolean }

export const findPostSessionPending = async (): Promise<PostSessionPending | null> => {
  const userId = await requireUserId()

  const { data: partial, error: partialError } = await supabase
    .from("session_feedback")
    .select("programme_session_number, programme_id")
    .eq("user_id", userId)
    .is("rpe", null)
    .not("programme_session_number", "is", null)
    .not("programme_id", "is", null)
    .order("completed_at", { ascending: false })
    .limit(1)
    .maybeSingle()
  if (partialError) console.error("[findPostSessionPending] session_feedback:", partialError)
  if (partial) {
    return {
      kind: "program",
      programme_id: partial.programme_id as string,
      session_number: partial.programme_session_number as number,
    }
  }

  return findActivityAwaitingQuestionnaire(userId)
}

async function findActivityAwaitingQuestionnaire(userId: string): Promise<PostSessionPending | null> {
  const today = new Date()
  const from = new Date(today)
  from.setDate(from.getDate() - 6)
  const { data, error } = await supabase
    .from("calendar_events")
    .select("id, type, event_date, end_time, meta")
    .eq("user_id", userId)
    .in("type", ACTIVITY_EVENT_TYPES)
    .gte("event_date", toLocalISODate(from))
    .lte("event_date", toLocalISODate(today))
    .not("end_time", "is", null)
  if (error) {
    console.error("[findPostSessionPending] calendar_events:", error)
    return null
  }
  const now = Date.now()
  const candidates = ((data ?? []) as Pick<CalendarEventRow, "id" | "type" | "event_date" | "end_time" | "meta">[])
    .filter((e) => !getActivityPostSessionAnswer(e))
    .map((e) => ({ eventId: e.id, endAt: getActivityEndAt(e)! }))
  const ended = candidates
    .filter((c) => c.endAt.getTime() <= now)
    .sort((a, b) => b.endAt.getTime() - a.endAt.getTime())
  if (ended.length > 0) return { kind: "activity", ...ended[0], locked: false }
  const upcoming = candidates
    .filter((c) => c.endAt.getTime() > now)
    .sort((a, b) => a.endAt.getTime() - b.endAt.getTime())
  return upcoming.length > 0 ? { kind: "activity", ...upcoming[0], locked: true } : null
}

/** 20:00 → « 20h00 » */
export const formatUnlockTime = (d: Date): string =>
  `${String(d.getHours()).padStart(2, "0")}h${String(d.getMinutes()).padStart(2, "0")}`

export type AnswerActivityResult =
  | { status: "ok"; answer: ActivityPostSessionAnswer }
  | { status: "already_answered"; answer: ActivityPostSessionAnswer }
  | { status: "locked" }
  | { status: "not_found" }

/** `input` null : « Je n'ai pas fait cette séance » (rien dans le monitoring). */
export const answerActivityPostSession = async (
  eventId: string,
  input: { duration: number; rpe: number; pleasure: number } | null,
): Promise<AnswerActivityResult> => {
  const { data, error } = await supabase.rpc("answer_activity_post_session", {
    p_event_id: eventId,
    p_done: input !== null,
    p_duration: input?.duration ?? null,
    p_rpe: input?.rpe ?? null,
    p_pleasure: input?.pleasure ?? null,
  })
  if (error) throw new Error(`Enregistrement du questionnaire impossible : ${error.message}`)
  return data as AnswerActivityResult
}

// ============================================
// DAILY WELLNESS (Hooper)
// ============================================

export const saveDailyWellness = async (data: DailyWellnessInput): Promise<DailyWellnessRow> => {
  const user = await requireUser()
  const { data: result, error } = await supabase
    .from("daily_wellness")
    .upsert([{ ...data, user_id: user.id, email: user.email }], { onConflict: "user_id,date" })
    .select().single()
  if (error) {
    console.error("[saveDailyWellness] error:", error)
    throw new Error(`Sauvegarde Hooper échouée : ${error.message}`)
  }
  return result as DailyWellnessRow
}

export const getDailyWellnessRange = async (days: number = 30): Promise<DailyWellnessRow[]> => {
  const userId = await requireUserId()
  // Fenêtre de `days` jours calendaires se terminant aujourd'hui (aujourd'hui - (days-1) .. aujourd'hui),
  // alignée sur buildDailySeries (calculations.ts) qui utilise la même borne pour les séries ACWR/Strain —
  // sinon les vues "Semaine" du Hooper et du Charge/Strain ne couvrent pas les mêmes semaines calendaires.
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - (days - 1))
  const { data, error } = await supabase
    .from("daily_wellness").select("*").eq("user_id", userId)
    .gte("date", toLocalISODate(startDate))
    .order("date", { ascending: true })
  if (error) throw new Error(`Lecture Hooper impossible : ${error.message}`)
  return (data ?? []) as DailyWellnessRow[]
}

export const getTodayWellness = async (): Promise<DailyWellnessRow | null> => {
  const userId = await requireUserId()
  const today = new Date().toISOString().split("T")[0]
  const { data, error } = await supabase
    .from("daily_wellness").select("*").eq("user_id", userId).eq("date", today).maybeSingle()
  if (error) { console.error("[getTodayWellness] error:", error); return null }
  return data as DailyWellnessRow | null
}

// ============================================
// TRAINING LOAD + SESSION FEEDBACK
// ============================================

export const savePostSession = async (data: PostSessionInput): Promise<{ feedback: SessionFeedbackRow; load: TrainingLoadRow }> => {
  const user = await requireUser()

  // 1. session_feedback
  const { data: feedbackResult, error: feedbackError } = await supabase
    .from("session_feedback")
    .upsert([{
      user_id: user.id,
      email: user.email,
      session_id: null,
      programme_id: data.programme_id ?? null,
      programme_session_number: data.programme_session_number ?? null,
      completed_at: new Date().toISOString(),
      actual_duration_minutes: data.session_duration,
      rpe: data.rpe,
      enjoyment: data.pleasure,
    }], { onConflict: 'user_id,programme_session_number,programme_id' })
    .select().single()

  if (feedbackError) {
    console.error("[savePostSession] feedback error:", feedbackError)
    throw new Error(`Sauvegarde feedback échouée : ${feedbackError.message}`)
  }

  // 1b. Mark the calendar event as done (safety net: saveSessionCompletion may have failed)
  if (data.programme_session_number) {
    const postSessionToday = toLocalISODate(new Date())
    supabase
      .from("calendar_events")
      .select("id, event_date")
      .eq("user_id", user.id)
      .eq("programme_session_number", data.programme_session_number)
      .eq("type", "seance-planned")
      .maybeSingle()
      .then(({ data: calEvent, error: calErr }) => {
        console.log(`[savePostSession] calendar lookup psn=${data.programme_session_number} → found=${JSON.stringify(calEvent)} err=${calErr?.message ?? "none"}`)
        if (calEvent) {
          const isLate = calEvent.event_date < postSessionToday
          supabase.from("calendar_events")
            .update(isLate ? { type: "seance-done", event_date: postSessionToday } : { type: "seance-done" })
            .eq("id", calEvent.id)
            .then(({ error: updErr }) => {
              console.log(`[savePostSession] calendar update → ${updErr ? "ERROR " + updErr.message : "OK"}`)
              if (!updErr && isLate) {
                cascadeReschedule(user.id, postSessionToday, data.programme_id)
              }
            })
        }
      })
  }

  // 1c. Déclencher la planification event-driven du prochain bloc (non bloquant)
  const completionDate = new Date().toLocaleDateString("fr-CA", { timeZone: "Europe/Paris" }) // YYYY-MM-DD
  supabase.functions
    .invoke("check-and-populate-next-block", {
      body: { user_id: user.id, completion_date: completionDate },
    })
    .then(({ error: populateError }) => {
      if (populateError) {
        console.error("[savePostSession] check-and-populate-next-block failed:", populateError)
      }
    })

  // 2. training_load — ADDITIONNÉE à la charge du jour (une ligne par jour) : une
  // activité du calendrier renseignée le même jour ne doit pas être effacée.
  const { data: loadResult, error: loadError } = await supabase
    .rpc("add_training_load", {
      p_date: data.date,
      p_duration: data.session_duration,
      p_rpe: data.rpe,
    })
    .single()

  if (loadError) {
    console.error("[savePostSession] load error:", loadError)
    throw new Error(`Sauvegarde training load échouée : ${loadError.message}`)
  }

  return { feedback: feedbackResult as SessionFeedbackRow, load: loadResult as TrainingLoadRow }
}

// Insertion partielle à la fin de séance (rpe=null) — le questionnaire post-séance complétera la ligne via upsert
// Returns the next training day (Mon/Tue/Thu/Fri) after the given ISO date string.
// Uses noon UTC to avoid DST boundary issues.
function nextTrainingDay(dateStr: string): string {
  const TRAINING_DAYS = [1, 2, 4, 5] // Mon, Tue, Thu, Fri
  const d = new Date(dateStr + "T12:00:00Z")
  d.setDate(d.getDate() + 1)
  while (!TRAINING_DAYS.includes(d.getUTCDay())) {
    d.setDate(d.getDate() + 1)
  }
  return d.toISOString().split("T")[0]
}

// When a late session is caught up on day `today`, every still-planned session — whether
// it's stuck in the past (never caught up on its own day) or sitting in the future — is
// reflowed forward in original order, one training day at a time. Cascade stops as soon
// as a natural gap absorbs the shift.
//
// `session_feedback` is the source of truth for completion — a `seance-planned` row can
// go stale and never get flipped to `seance-done` (legacy data, a failed update, etc.).
// Cross-checking against it before reflowing prevents such orphaned rows from occupying
// cascade slots ahead of genuinely pending sessions. `programmeId`, when known, further
// scopes both queries so a completed session number from a different programme can't be
// mistaken for a pending one (programme_session_number resets per programme).
async function cascadeReschedule(userId: string, today: string, programmeId?: string | null): Promise<void> {
  let plannedQuery = supabase
    .from("calendar_events")
    .select("id, event_date, programme_session_number")
    .eq("user_id", userId)
    .eq("type", "seance-planned")
  if (programmeId) plannedQuery = plannedQuery.eq("session_ref", programmeId)
  const { data: allPlanned } = await plannedQuery.order("event_date", { ascending: true })

  if (!allPlanned || allPlanned.length === 0) return

  const psns = allPlanned
    .map(e => e.programme_session_number)
    .filter((n): n is number => n !== null)

  let feedbackQuery = supabase
    .from("session_feedback")
    .select("programme_session_number")
    .eq("user_id", userId)
    .in("programme_session_number", psns)
  if (programmeId) feedbackQuery = feedbackQuery.eq("programme_id", programmeId)
  const { data: doneRows } = psns.length > 0 ? await feedbackQuery : { data: [] as { programme_session_number: number | null }[] }

  const completedPsns = new Set(
    (doneRows ?? []).map(r => r.programme_session_number).filter((n): n is number => n !== null)
  )
  const pending = (allPlanned as { id: string; event_date: string; programme_session_number: number | null }[])
    .filter(e => e.programme_session_number === null || !completedPsns.has(e.programme_session_number))

  if (pending.length === 0) return

  // today is now consumed by the catch-up; first available slot is the next training day
  let cursor = nextTrainingDay(today)
  const updates: { id: string; event_date: string }[] = []

  for (const event of pending) {
    if (event.event_date < cursor) {
      // Session sits before the cursor → must move to cursor
      updates.push({ id: event.id, event_date: cursor })
      cursor = nextTrainingDay(cursor)
    } else {
      // Session is already at or beyond cursor → natural gap, advance cursor past it
      cursor = nextTrainingDay(event.event_date)
    }
  }

  if (updates.length === 0) return

  await Promise.all(
    updates.map(u =>
      supabase.from("calendar_events").update({ event_date: u.event_date }).eq("id", u.id)
    )
  )
}

export const saveSessionCompletion = async (data: {
  programme_id: string
  programme_session_number: number
  session_meta?: SessionMeta
}): Promise<boolean> => {
  const user = await requireUser()

  // Check if already completed — avoids duplicate pushes when session is replayed
  const { data: existing } = await supabase
    .from("session_feedback")
    .select("id")
    .eq("user_id", user.id)
    .eq("programme_id", data.programme_id)
    .eq("programme_session_number", data.programme_session_number)
    .maybeSingle()

  if (existing) {
    console.log(`[saveSessionCompletion] already completed psn=${data.programme_session_number}, skipping pushes`)
    return false
  }

  const { error } = await supabase
    .from("session_feedback")
    .upsert([{
      user_id: user.id,
      email: user.email,
      session_id: null,
      programme_id: data.programme_id,
      programme_session_number: data.programme_session_number,
      completed_at: new Date().toISOString(),
      actual_duration_minutes: null,
      rpe: null,
      enjoyment: null,
      session_meta: data.session_meta ?? null,
    }], { ignoreDuplicates: true })
  if (error) {
    console.error("[saveSessionCompletion] error:", error)
    return false
  }

  // Mark the planned calendar event as done and detect late catch-up
  const today = toLocalISODate(new Date())
  const { data: calEvent, error: calLookupErr } = await supabase
    .from("calendar_events")
    .select("id, event_date, type")
    .eq("user_id", user.id)
    .eq("programme_session_number", data.programme_session_number)
    .eq("type", "seance-planned")
    .maybeSingle()

  console.log(`[saveSessionCompletion] calendar lookup psn=${data.programme_session_number} → found=${JSON.stringify(calEvent)} err=${calLookupErr?.message ?? "none"}`)

  if (calEvent) {
    const isLate = calEvent.event_date < today
    const { error: calUpdateErr } = await supabase
      .from("calendar_events")
      .update(isLate ? { type: "seance-done", event_date: today } : { type: "seance-done" })
      .eq("id", calEvent.id)

    console.log(`[saveSessionCompletion] calendar update → ${calUpdateErr ? "ERROR " + calUpdateErr.message : "OK"}`)

    // If the session was done later than planned, move it to the catch-up day and
    // shift all compressed future sessions forward — regardless of whether the
    // catch-up day already has a planned session or is empty.
    if (isLate) {
      await cascadeReschedule(user.id, today, data.programme_id)
    }
  }

  return true
}

// Écriture en batch, une seule fois en fin de séance (miroir de saveSessionCompletion) —
// jamais un insert par tap "Série terminée", pour qu'un aller-retour décoché/recoché
// sur un drop set ne produise pas de doublon. ignoreDuplicates protège en plus un replay
// de la même séance (cf. le guard `isNewCompletion` dans SessionPage).
export const saveSessionSetLogs = async (rows: SessionSetLogInput[]): Promise<void> => {
  if (rows.length === 0) return
  const user = await requireUser()
  const { error } = await supabase
    .from("session_set_logs")
    .upsert(
      rows.map(r => ({ ...r, user_id: user.id })),
      { onConflict: "user_id,programme_session_number,round,exercise_index,movement_index", ignoreDuplicates: true }
    )
  if (error) console.error("[saveSessionSetLogs] error:", error)
}

// Nombre de semaines de PROGRAMME lues en arrière de la semaine courante : la
// dernière semaine complète peut être jusqu'à MAX_REFERENCE_LOOKBACK_WEEKS (4)
// semaines derrière la courante, et sa propre référence encore 4 plus loin
// (cf. calculations.ts → compareProgrammeWeeks).
const PROGRAMME_WEEKS_LOOKBACK = 8

export interface ProgrammePlanSession {
  session_number: number
  week: number
  type: string | null
  is_deload: boolean
}

export interface ProgrammeSetLogs {
  // Séances prescrites du programme en cours (celui de la dernière série loggée) :
  // sert à savoir combien de séances compte chaque semaine et lesquelles sont
  // des décharges, indépendamment de ce qui a été fait.
  plan: ProgrammePlanSession[]
  logs: SessionSetLogRow[]
}

// Séries loggées du programme en cours, découpées par SEMAINE DE PROGRAMME
// (program_sessions.week) et non par date : une séance décalée d'un jour ou
// d'une semaine reste dans sa semaine de programme, et la décharge est celle
// que définit le catalogue. Lecture par numéros de séance, pas par fenêtre de
// jours — une cadence irrégulière peut étaler une semaine de programme sur
// plusieurs semaines calendaires.
// Pas de FK entre session_set_logs et program_sessions (colonnes programme_id/
// programme_session_number en texte/int libres) — donc pas de jointure
// PostgREST automatique : on enrichit chaque ligne à la main (is_deload,
// session_type, programme_week).
export const getProgrammeSetLogs = async (): Promise<ProgrammeSetLogs> => {
  const empty: ProgrammeSetLogs = { plan: [], logs: [] }
  const userId = await requireUserId()

  const { data: latest, error: latestError } = await supabase
    .from("session_set_logs")
    .select("programme_id")
    .eq("user_id", userId)
    .order("completed_at", { ascending: false })
    .limit(1)
  if (latestError) { console.error("[getProgrammeSetLogs] latest error:", latestError); return empty }
  const programmeId = latest?.[0]?.programme_id as string | undefined
  if (!programmeId) return empty

  const { data: planData, error: planError } = await supabase
    .from("program_sessions")
    .select("session_number, week, type, is_deload")
    .eq("programme_id", programmeId)
    .order("session_number", { ascending: true })
  if (planError) { console.error("[getProgrammeSetLogs] plan error:", planError); return empty }
  const plan = (planData ?? []) as ProgrammePlanSession[]
  const bySession = new Map(plan.map((s) => [s.session_number, s]))

  // Semaine courante = la plus avancée ayant au moins une série loggée (pas la
  // dernière dans le temps : rattraper une séance oubliée ne doit pas faire
  // reculer la semaine courante). `week` croît avec `session_number` dans le
  // catalogue : le plus grand numéro loggé suffit (une ligne, jamais plafonnée).
  const { data: furthest, error: furthestError } = await supabase
    .from("session_set_logs")
    .select("programme_session_number")
    .eq("user_id", userId)
    .eq("programme_id", programmeId)
    .order("programme_session_number", { ascending: false })
    .limit(1)
  if (furthestError) { console.error("[getProgrammeSetLogs] furthest error:", furthestError); return empty }
  const currentWeek = bySession.get(furthest?.[0]?.programme_session_number as number)?.week
  if (currentWeek === undefined) return { plan, logs: [] }

  const sessionNumbers = plan
    .filter((s) => s.week >= currentWeek - PROGRAMME_WEEKS_LOOKBACK && s.week <= currentWeek)
    .map((s) => s.session_number)

  // Paginé : PostgREST plafonne silencieusement à 1000 lignes par réponse.
  const PAGE = 1000
  const rows: Omit<SessionSetLogRow, "is_deload" | "session_type" | "programme_week">[] = []
  for (let from = 0; ; from += PAGE) {
    const { data, error } = await supabase
      .from("session_set_logs")
      .select("*")
      .eq("user_id", userId)
      .eq("programme_id", programmeId)
      .in("programme_session_number", sessionNumbers)
      .order("completed_at", { ascending: true })
      .order("id", { ascending: true })
      .range(from, from + PAGE - 1)
    if (error) { console.error("[getProgrammeSetLogs] error:", error); return { plan, logs: [] } }
    rows.push(...((data ?? []) as typeof rows))
    if (!data || data.length < PAGE) break
  }

  const logs = rows.map((r) => {
    const s = bySession.get(r.programme_session_number)
    return {
      ...r,
      is_deload: s?.is_deload ?? false,
      session_type: s?.type ?? null,
      programme_week: s?.week ?? null,
    }
  })
  return { plan, logs }
}

export const getTrainingLoads = async (days: number = 28): Promise<TrainingLoadRow[]> => {
  const userId = await requireUserId()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - days)
  const { data, error } = await supabase
    .from("training_load").select("*").eq("user_id", userId)
    .gte("date", toLocalISODate(startDate))
    .order("date", { ascending: true })
  if (error) throw new Error(`Lecture training load impossible : ${error.message}`)
  return (data ?? []) as TrainingLoadRow[]
}

// ── Session 5 : historique de toutes les séances complétées ──
export const listSessionFeedbacks = async (limit: number = 50): Promise<SessionFeedbackRow[]> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("session_feedback").select("*").eq("user_id", userId)
    .order("completed_at", { ascending: false }).limit(limit)
  if (error) throw new Error(`Lecture historique séances impossible : ${error.message}`)
  return (data ?? []) as SessionFeedbackRow[]
}

/**
 * Séances libres passées, pour l'historique — elles vivent dans `calendar_events`, pas
 * dans `session_feedback` (réservée aux séances du programme). « Passée » = jour
 * antérieur, ou aujourd'hui à heure de fin atteinte ; celles marquées « Je n'ai pas fait
 * cette séance » sont écartées.
 */
export const listFreeSessions = async (limit: number = 50): Promise<CalendarEventRow[]> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("calendar_events").select("*").eq("user_id", userId)
    .eq("type", "seance-libre")
    .lte("event_date", toLocalISODate(new Date()))
    .order("event_date", { ascending: false }).limit(limit)
  if (error) throw new Error(`Lecture des séances libres impossible : ${error.message}`)
  const now = Date.now()
  return ((data ?? []) as CalendarEventRow[]).filter((e) => {
    if (getActivityPostSessionAnswer(e)?.status === "skipped") return false
    const endAt = getActivityEndAt(e)
    return !endAt || endAt.getTime() <= now
  })
}

// ============================================
// ATHLETE PROFILE (1RM)
// ============================================

const ATHLETE_PROFILE_NUMERIC_FIELDS = [
  "rm_bench_press", "rm_back_squat", "rm_deadlift", "rm_overhead_press", "rm_pull_up",
] as const

/**
 * `rm_*` est passé de `integer` à `numeric` (migration `20261001120000`) : le test de
 * force en séance arrondit volontairement au demi-kilo (`Math.round(x*2)/2` dans
 * `recordTestResult`), et la saisie manuelle (`RecordHistoryPage`) accepte déjà des
 * décimales au clavier — un entier ne pouvait plus les recevoir.
 *
 * Même conséquence, même remède que `parseBodyMeasurementsRow` ci-dessous : PostgREST
 * sérialise une colonne `numeric` en chaîne JSON, jamais en nombre JS. Sans cette
 * normalisation, `typeof x === "number"` ment pour toute valeur renseignée — exactement
 * le garde utilisé par `comparableValue()`/`rmValueFor()` (strengthStandards.ts,
 * strengthTargets.ts) pour décider si un 1RM existe, qui rendrait alors un radar et des
 * cibles de bloc vides en silence pour CHAQUE client dès cette migration appliquée,
 * faute de cette étape. Une valeur absente reste `null` dans les deux cas.
 */
function parseAthleteProfileRow(raw: Record<string, unknown>): AthleteProfileRow {
  const parsed = { ...raw } as unknown as Record<string, unknown>
  for (const field of ATHLETE_PROFILE_NUMERIC_FIELDS) {
    const v = raw[field]
    parsed[field] = v == null ? null : Number(v)
  }
  return parsed as unknown as AthleteProfileRow
}

export const getAthleteProfile = async (): Promise<AthleteProfileRow | null> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("athlete_profiles").select("*").eq("user_id", userId).maybeSingle()
  if (error) { console.error("[getAthleteProfile] error:", error); return null }
  return data ? parseAthleteProfileRow(data as Record<string, unknown>) : null
}

export const upsertAthleteProfile = async (input: AthleteProfileInput): Promise<AthleteProfileRow> => {
  const user = await requireUser()
  const { data: existing } = await supabase
    .from("athlete_profiles").select("id").eq("user_id", user.id).maybeSingle()

  if (existing) {
    const { data, error } = await supabase
      .from("athlete_profiles").update(input).eq("user_id", user.id).select().single()
    if (error) throw new Error(`Mise à jour 1RM échouée : ${error.message}`)
    return parseAthleteProfileRow(data as Record<string, unknown>)
  }

  const { data, error } = await supabase
    .from("athlete_profiles")
    .insert([{ ...input, user_id: user.id, email: user.email }])
    .select().single()
  if (error) throw new Error(`Création profil athlète échouée : ${error.message}`)
  return parseAthleteProfileRow(data as Record<string, unknown>)
}

// ============================================
// BLESSURES DÉCLARÉES (lot 3) — données de santé, accord explicite obligatoire
// ============================================

export type InjuryZone =
  | "epaule" | "coude" | "poignet" | "cou" | "haut_dos"
  | "bas_dos" | "hanche" | "genou" | "cheville" | "autre"

/** Le critère est la douleur : `genante` = « Elle me fait encore mal ou me gêne ». */
export type InjuryStatus = "guerie" | "genante"

export type AthleteInjuryRow = {
  id: string
  user_id: string
  zone: InjuryZone
  /** Libellé saisi par le client, seulement pour la zone « autre ». */
  zone_other: string | null
  status: InjuryStatus
  source: "diagnostic" | "profil" | "admin"
  created_at: string
  updated_at: string
}

export type InjuryDeclaration = { zone: InjuryZone; status: InjuryStatus; zone_other?: string | null }

export const getAthleteInjuries = async (): Promise<AthleteInjuryRow[]> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("athlete_injuries").select("*").eq("user_id", userId).order("created_at", { ascending: true })
  if (error) throw new Error(`Lecture des blessures impossible : ${error.message}`)
  return (data ?? []) as AthleteInjuryRow[]
}

/**
 * Fin du diagnostic, case d'accord cochée : l'accord (heure du serveur), les blessures
 * et la précision libre partent ensemble, en une transaction (`save_diagnostic_injuries`).
 * La précision va dans `athlete_profiles.injuries` sans jamais écraser un texte existant.
 */
export const saveDiagnosticInjuries = async (
  consentVersion: string,
  injuries: InjuryDeclaration[],
  note: string | null,
): Promise<void> => {
  const { error } = await supabase.rpc("save_diagnostic_injuries", {
    p_version: consentVersion,
    p_injuries: injuries,
    p_note: note,
  })
  if (error) throw new Error(`Enregistrement des blessures impossible : ${error.message}`)
}

/** Accord donné hors diagnostic (éditeur « Mon programme › Blessures »). */
export const recordInjuryConsent = async (consentVersion: string): Promise<void> => {
  const { error } = await supabase.rpc("record_injury_consent", { p_version: consentVersion })
  if (error) throw new Error(`Enregistrement de l'accord impossible : ${error.message}`)
}

/**
 * Retrait de l'accord : blessures déclarées, texte libre et preuve d'accord sont
 * effacés en UNE transaction côté base (`revoke_injury_consent`) — jamais à moitié.
 */
export const revokeInjuryConsent = async (): Promise<void> => {
  const { error } = await supabase.rpc("revoke_injury_consent")
  if (error) throw new Error(`Retrait de l'accord impossible : ${error.message}`)
}

export const adminRevokeInjuryConsent = async (userId: string): Promise<void> => {
  const { error } = await supabase.rpc("admin_revoke_injury_consent", { p_user_id: userId })
  if (error) throw new Error(`adminRevokeInjuryConsent : ${error.message}`)
}

// ============================================
// WEIGHT HISTORY
// ============================================

/**
 * Même normalisation que `parseBodyMeasurementsRow` (voir plus bas) : `weight` est une
 * colonne `numeric`, PostgREST la sérialise en chaîne JSON plutôt qu'en nombre. Sans ce
 * passage, un garde strict comme `typeof weight === "number"` ment en permanence pour une
 * valeur pourtant bien présente — c'était le bug de `getCurrentBodyweightKg`, qui retombait
 * toujours sur `null` faute de ce test qui ne passait jamais.
 */
function parseWeightHistoryRow(raw: Record<string, unknown>): WeightHistoryRow {
  const parsed = { ...raw } as unknown as Record<string, unknown>
  parsed.weight = raw.weight == null ? null : Number(raw.weight)
  return parsed as unknown as WeightHistoryRow
}

export const addWeightEntry = async (date: string, weight: number, note?: string): Promise<WeightHistoryRow> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("weight_history")
    .upsert([{ user_id: userId, date, weight, note: note ?? null }], { onConflict: "user_id,date" })
    .select().single()
  if (error) throw new Error(`Sauvegarde poids échouée : ${error.message}`)
  return parseWeightHistoryRow(data as Record<string, unknown>)
}

export const getWeightHistory = async (days: number = 90): Promise<WeightHistoryRow[]> => {
  const userId = await requireUserId()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - days)
  const { data, error } = await supabase
    .from("weight_history").select("*").eq("user_id", userId)
    .gte("date", startDate.toISOString().split("T")[0])
    .order("date", { ascending: true })
  if (error) throw new Error(`Lecture poids impossible : ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(parseWeightHistoryRow)
}

/**
 * Poids de corps actuel pour un calcul de charge (lest/assistance) : dernière pesée
 * de `weight_history` en priorité (poids réel le plus récent), repli sur
 * `wizard_submissions.weight` (poids saisi à l'onboarding) si l'athlète n'a jamais
 * pesé depuis.
 */
export const getCurrentBodyweightKg = async (userId: string): Promise<number | null> => {
  const { data: lastWeighIn } = await supabase
    .from("weight_history")
    .select("weight")
    .eq("user_id", userId)
    .order("date", { ascending: false })
    .limit(1)
    .maybeSingle()
  if (lastWeighIn) {
    const w = Number(lastWeighIn.weight)
    if (Number.isFinite(w)) return w
  }

  const { data: wizard } = await supabase
    .from("wizard_submissions")
    .select("weight")
    .eq("user_id", userId)
    .maybeSingle()
  if (wizard) {
    const w = Number(wizard.weight)
    if (Number.isFinite(w)) return w
  }
  return null
}

// ============================================
// BODY MEASUREMENTS (Lot 2a — Profil)
// ============================================

const BODY_MEASUREMENT_NUMERIC_FIELDS = [
  "waist_cm", "neck_cm", "hips_cm", "shoulders_cm", "chest_cm",
  "arm_right_cm", "arm_left_cm", "forearm_cm", "thigh_cm", "calf_cm",
  "body_fat_pct_manual",
] as const

/**
 * PostgREST sérialise les colonnes `numeric` en chaînes JSON, jamais en nombre JS
 * (pour préserver la précision arbitraire du type — cf. discussion PostgREST/postgrest#2596).
 * Sans cette normalisation, une valeur renseignée arrive en string ("70" au lieu de 70) :
 * les opérateurs arithmétiques (+, -, *, /) la coercent silencieusement et ne cassent pas,
 * mais `typeof x === "number"` ment et toute vérification stricte (voir les gardes de
 * src/lib/bodyComposition.ts) devient invérifiable. Une valeur absente reste `null` dans
 * les deux cas (NULL SQL → JSON null quel que soit le type de la colonne) — cette fonction
 * ne change donc rien au comportement d'une mesure manquante, seulement au typage d'une
 * mesure présente.
 */
function parseBodyMeasurementsRow(raw: Record<string, unknown>): BodyMeasurementsRow {
  const parsed = { ...raw } as unknown as Record<string, unknown>
  for (const field of BODY_MEASUREMENT_NUMERIC_FIELDS) {
    const v = raw[field]
    parsed[field] = v == null ? null : Number(v)
  }
  return parsed as unknown as BodyMeasurementsRow
}

export const addBodyMeasurementsEntry = async (
  date: string,
  input: BodyMeasurementsInput
): Promise<BodyMeasurementsRow> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("body_measurements")
    .upsert([{ user_id: userId, date, ...input }], { onConflict: "user_id,date" })
    .select().single()
  if (error) throw new Error(`Sauvegarde du relevé échouée : ${error.message}`)
  return parseBodyMeasurementsRow(data as Record<string, unknown>)
}

export const getBodyMeasurementsHistory = async (days: number = 730): Promise<BodyMeasurementsRow[]> => {
  const userId = await requireUserId()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - days)
  const { data, error } = await supabase
    .from("body_measurements").select("*").eq("user_id", userId)
    .gte("date", startDate.toISOString().split("T")[0])
    .order("date", { ascending: true })
  if (error) throw new Error(`Lecture des mesures impossible : ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(parseBodyMeasurementsRow)
}

// ============================================
// STRENGTH BLOCK TARGETS (Lot 2b — Radar de force)
// ============================================

export type StrengthBlockTargetRow = {
  id: string
  user_id: string
  programme_id: string
  block_start_date: string
  movement: StrengthMovement
  /** kg pour les 4 mouvements à la barre, ratio de charge totale pour la traction lestée. */
  initial_value: number
  initial_bodyweight_kg: number
  initial_percent: number
  zone: TargetZone
  target_value: number
  mid_block_adjusted: boolean
  mid_block_measured_at: string | null
  created_at: string
  updated_at: string
}

export type StrengthBlockTargetInput = {
  programme_id: string
  block_start_date: string
  movement: StrengthMovement
  initial_value: number
  initial_bodyweight_kg: number
  initial_percent: number
  zone: TargetZone
  target_value: number
}

const STRENGTH_TARGET_NUMERIC_FIELDS = [
  "initial_value", "initial_bodyweight_kg", "initial_percent", "target_value",
] as const

/**
 * Même normalisation que `parseBodyMeasurementsRow` : PostgREST sérialise les colonnes
 * `numeric` en chaînes JSON. Sans ce passage, `initial_percent` arriverait en "0.08" et
 * `pct * 1.5` donnerait bien 0.12 (coercition silencieuse), mais toute comparaison stricte
 * et tout `toFixed` en amont mentiraient. Voir le commentaire détaillé de
 * `parseBodyMeasurementsRow` plus haut dans ce fichier.
 */
function parseStrengthTargetRow(raw: Record<string, unknown>): StrengthBlockTargetRow {
  const parsed = { ...raw } as unknown as Record<string, unknown>
  for (const field of STRENGTH_TARGET_NUMERIC_FIELDS) {
    const v = raw[field]
    parsed[field] = v == null ? null : Number(v)
  }
  return parsed as unknown as StrengthBlockTargetRow
}

/**
 * Séances de test du programme : [test initial, test intermédiaire, test final].
 * Colonne alimentée par la migration 20260906120000 + `scripts/seed-programs.js`.
 * Renvoie un tableau vide si le programme est inconnu ou la colonne pas encore backfillée —
 * l'appelant traite ce cas comme « pas de cible à calculer », jamais comme une erreur.
 */
export const getProgramTestingSessions = async (programmeId: string): Promise<number[]> => {
  const { data, error } = await supabase
    .from("programs").select("testing_sessions").eq("programme_id", programmeId).maybeSingle()
  if (error) {
    console.error("[getProgramTestingSessions] error:", error)
    return []
  }
  const raw = data?.testing_sessions
  return Array.isArray(raw) ? (raw as number[]).filter((n) => Number.isFinite(n)) : []
}

/** Cibles du bloc en cours pour ce client (une ligne par mouvement testé). */
export const getStrengthBlockTargets = async (
  programmeId: string,
  blockStartDate: string
): Promise<StrengthBlockTargetRow[]> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("strength_block_targets").select("*")
    .eq("user_id", userId)
    .eq("programme_id", programmeId)
    .eq("block_start_date", blockStartDate)
  if (error) {
    console.error("[getStrengthBlockTargets] error:", error)
    return []
  }
  return ((data ?? []) as Record<string, unknown>[]).map(parseStrengthTargetRow)
}

/**
 * Crée les cibles du bloc. `ignoreDuplicates` : une cible de bloc se fige au test initial
 * et ne se recalcule jamais depuis le 1RM courant — si la ligne existe déjà (deuxième
 * ouverture du radar, course entre deux onglets), on garde la version d'origine.
 */
export const insertStrengthBlockTargets = async (
  inputs: StrengthBlockTargetInput[]
): Promise<StrengthBlockTargetRow[]> => {
  if (inputs.length === 0) return []
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("strength_block_targets")
    .upsert(
      inputs.map((i) => ({ ...i, user_id: userId })),
      { onConflict: "user_id,programme_id,block_start_date,movement", ignoreDuplicates: true }
    )
    .select()
  if (error) throw new Error(`Création des cibles de force échouée : ${error.message}`)
  return ((data ?? []) as Record<string, unknown>[]).map(parseStrengthTargetRow)
}

/**
 * Applique l'ajustement du test intermédiaire à une cible existante. Seuls `target_value`
 * et les marqueurs d'ajustement bougent : `initial_value` et `initial_percent` restent
 * ceux du test initial, puisque c'est de là que repart tout recalcul ultérieur.
 */
export const applyMidBlockAdjustment = async (
  targetId: string,
  newTargetValue: number,
  measuredOn: string
): Promise<StrengthBlockTargetRow | null> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("strength_block_targets")
    .update({
      target_value: newTargetValue,
      mid_block_adjusted: true,
      mid_block_measured_at: measuredOn,
      updated_at: new Date().toISOString(),
    })
    .eq("id", targetId)
    .eq("user_id", userId)
    .select().single()
  if (error) {
    console.error("[applyMidBlockAdjustment] error:", error)
    return null
  }
  return parseStrengthTargetRow(data as Record<string, unknown>)
}

/**
 * Marque le test intermédiaire comme constaté sans rehausser la cible (mesure sous le
 * seuil de 90 %). Évite de réévaluer le déclencheur à chaque ouverture du radar avec un
 * 1RM qui, lui, continue de bouger.
 */
export const markMidBlockChecked = async (
  targetId: string,
  measuredOn: string
): Promise<void> => {
  const userId = await requireUserId()
  const { error } = await supabase
    .from("strength_block_targets")
    .update({ mid_block_measured_at: measuredOn, updated_at: new Date().toISOString() })
    .eq("id", targetId)
    .eq("user_id", userId)
  if (error) console.error("[markMidBlockChecked] error:", error)
}

// ============================================
// WEEKLY METRICS
// ============================================

export const getWeeklyMetrics = async (weeks: number = 8) => {
  const userId = await requireUserId()
  const startDate = new Date()
  startDate.setDate(startDate.getDate() - weeks * 7)
  const { data, error } = await supabase
    .from("weekly_metrics").select("*").eq("user_id", userId)
    .gte("week_start_date", startDate.toISOString().split("T")[0])
    .order("week_start_date", { ascending: true })
  if (error) { console.error("Erreur weekly_metrics:", error); return [] }
  return data
}

// ============================================
// SUBSCRIPTIONS + PURCHASES (Session 6)
// ============================================

export type SubscriptionStatus = "trialing" | "active" | "past_due" | "canceled" | "incomplete"

export type SubscriptionPlan = "autonomie" | "coaching" | "premium" | "lyon"

/** Origine du droit : essai de l'app, abonnement Stripe, ou accès donné à la main. */
export type SubscriptionSource = "trial" | "stripe" | "manual"

export type SubscriptionRow = {
  id: string
  user_id: string
  plan: SubscriptionPlan
  source: SubscriptionSource
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
  status: SubscriptionStatus
  price_id: string | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  trial_started_at: string | null
  trial_ends_at: string | null
  trial_ended_at: string | null
  /** @deprecated Ancien essai « 7 jours OU 1 séance » — plus lu ni écrit depuis le lot 2. */
  trial_consumed: boolean
  created_at: string
  updated_at: string
}

export type PurchaseRow = {
  id: string
  user_id: string
  product_id: "ebook"
  stripe_checkout_session_id: string | null
  stripe_payment_intent_id: string | null
  amount_cents: number
  currency: string
  purchased_at: string
  created_at: string
}

export const getSubscription = async (): Promise<SubscriptionRow | null> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("subscriptions").select("*").eq("user_id", userId).maybeSingle()
  if (error) { console.error("[getSubscription] error:", error); return null }
  return data as SubscriptionRow | null
}

/**
 * Droit d'accès calculé en base (vue `access_rights`) — seule source du droit,
 * partagée avec les crons de push. `null` = aucune ligne d'abonnement.
 */
export type AccessRightsRow = {
  user_id: string
  plan: SubscriptionPlan
  source: SubscriptionSource
  status: SubscriptionStatus
  /** J0 : activation de l'essai. NULL hors essai. */
  trial_started_at: string | null
  /** Matin de J8, 07:00 heure de Paris (D12). */
  trial_ends_at: string | null
  /** Moment où la fin d'essai a été traitée par le cron. NULL tant qu'elle ne l'est pas. */
  trial_ended_at: string | null
  in_trial: boolean
  has_access: boolean
}

export const getAccessRights = async (): Promise<AccessRightsRow | null> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("access_rights").select("*").eq("user_id", userId).maybeSingle()
  if (error) { console.error("[getAccessRights] error:", error); return null }
  return data as AccessRightsRow | null
}

/** Levée quand l'adresse a déjà bénéficié de la semaine d'essai (D6). */
export class TrialAlreadyUsedError extends Error {}

/**
 * Active la semaine d'essai — un seul appel serveur (edge function `start-trial`).
 *
 * Elle pose l'échéance (matin de J8, 07:00 Paris), vérifie la règle « un essai
 * par email » et planifie la première semaine de séances RÉELLES du programme.
 * Le navigateur n'écrit plus rien et n'enchaîne plus deux appels dont le second
 * n'était pas attendu (l'ancienne `create-trial-session`).
 */
export const startTrial = async (): Promise<SubscriptionRow> => {
  const { data, error } = await supabase.functions.invoke("start-trial", { body: {} })
  if (error) {
    // Le corps de la réponse porte le motif ; `error.message` seul dirait juste
    // « non-2xx status code », illisible pour le client.
    const detail = await error.context?.json?.().catch(() => null)
    if (detail?.code === "23505") throw new TrialAlreadyUsedError(detail.error)
    throw new Error(detail?.error ?? `Activation de l'essai impossible : ${error.message}`)
  }
  return (data as { subscription: SubscriptionRow }).subscription
}

export const getPurchases = async (): Promise<PurchaseRow[]> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("purchases").select("*").eq("user_id", userId)
    .order("purchased_at", { ascending: false })
  if (error) throw new Error(`Lecture achats impossible : ${error.message}`)
  return (data ?? []) as PurchaseRow[]
}

// ============================================
// NOTIFICATIONS (Session 5)
// ============================================

export const listNotifications = async (limit: number = 50): Promise<NotificationRow[]> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("notifications").select("*").eq("user_id", userId)
    .order("created_at", { ascending: false }).limit(limit)
  if (error) throw new Error(`Lecture notifications impossible : ${error.message}`)
  return (data ?? []) as NotificationRow[]
}

export const getUnreadNotificationCount = async (): Promise<number> => {
  const userId = await requireUserId()
  const { count, error } = await supabase
    .from("notifications").select("*", { count: "exact", head: true })
    .eq("user_id", userId).eq("read", false)
  if (error) { console.error("[getUnreadNotificationCount] error:", error); return 0 }
  return count ?? 0
}

export const createNotification = async (input: NotificationInput): Promise<NotificationRow> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("notifications").insert([{ ...input, user_id: userId }]).select().single()
  if (error) throw new Error(`Création notification impossible : ${error.message}`)
  return data as NotificationRow
}

export const markNotificationRead = async (id: string): Promise<void> => {
  const { error } = await supabase
    .from("notifications").update({ read: true }).eq("id", id)
  if (error) throw new Error(`Mise à jour notification impossible : ${error.message}`)
}

export const markAllNotificationsRead = async (): Promise<void> => {
  const userId = await requireUserId()
  const { error } = await supabase
    .from("notifications").update({ read: true })
    .eq("user_id", userId).eq("read", false)
  if (error) throw new Error(`Mise à jour notifications impossible : ${error.message}`)
}

export const deleteNotification = async (id: string): Promise<void> => {
  const { error } = await supabase.from("notifications").delete().eq("id", id)
  if (error) throw new Error(`Suppression notification impossible : ${error.message}`)
}

/**
 * Repli pour une notification push sans identifiant de cloche (envoyée avant le
 * 04/10) : son type désigne la famille d'entrées, la plus récente non lue est marquée.
 */
const PUSH_TYPE_TO_NOTIFICATION: Record<string, { type: NotificationType; actionUrl?: string }> = {
  session_end:         { type: "post-session", actionUrl: "/dashboard" },
  post_session_form:   { type: "post-session", actionUrl: "/post-session" },
  hooper_morning:      { type: "hooper" },
  weight_reminder:     { type: "weight" },
  streak_achievements: { type: "achievement" },
  trial_ending:        { type: "reminder" },
  trial_ended:         { type: "reminder" },
}

/**
 * Clic sur une notification push : marque comme lue l'entrée de cloche qui lui
 * correspond. Depuis le 04/10, send-push transmet l'identifiant exact de l'entrée
 * (`notification_id`) ; avant, il ne transmettait même pas le type, et la cloche
 * restait « non lue » après le clic. Sans effet si personne n'est connecté.
 */
export const markPushNotificationRead = async (
  notificationId: string | null,
  pushType: string | null,
): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) return

  if (notificationId) {
    const { error } = await supabase
      .from("notifications").update({ read: true })
      .eq("id", notificationId).eq("user_id", user.id)
    if (error) console.error("[markPushNotificationRead] error:", error)
    return
  }

  const target = pushType ? PUSH_TYPE_TO_NOTIFICATION[pushType] : undefined
  if (!target) return
  let query = supabase
    .from("notifications").select("id")
    .eq("user_id", user.id).eq("type", target.type).eq("read", false)
  if (target.actionUrl) query = query.eq("action_url", target.actionUrl)
  const { data: notif } = await query
    .order("created_at", { ascending: false }).limit(1).maybeSingle()
  if (notif) {
    const { error } = await supabase.from("notifications").update({ read: true }).eq("id", notif.id)
    if (error) console.error("[markPushNotificationRead] error:", error)
  }
}

// ============================================
// PUSH SUBSCRIPTIONS (Session 7)
// ============================================

export type PushSubscriptionRow = {
  id: string
  user_id: string
  endpoint: string
  p256dh: string
  auth: string
  user_agent: string | null
  created_at: string
}

export type NotificationPreferencesRow = {
  user_id: string
  hooper_morning: boolean
  /** Colonne encore en base, plus lue ni écrite : le bilan de la semaine n'est plus envoyé (16/10). */
  weekly_recap: boolean
  weight_reminder: boolean
  session_end: boolean
  streak_achievements: boolean
  post_session_form: boolean
  created_at: string
  updated_at: string
}

export type NotificationPreferencesInput = {
  hooper_morning?: boolean
  weight_reminder?: boolean
  session_end?: boolean
  streak_achievements?: boolean
  post_session_form?: boolean
}

export const savePushSubscription = async (sub: PushSubscription): Promise<PushSubscriptionRow> => {
  const userId = await requireUserId()
  const json = sub.toJSON()
  const { data, error } = await supabase
    .from("push_subscriptions")
    .upsert([{
      user_id: userId,
      endpoint: sub.endpoint,
      p256dh: json.keys?.["p256dh"] ?? "",
      auth: json.keys?.["auth"] ?? "",
      user_agent: navigator.userAgent,
    }], { onConflict: "user_id,endpoint" })
    .select().single()
  if (error) throw new Error(`Sauvegarde souscription push échouée : ${error.message}`)
  return data as PushSubscriptionRow
}

/**
 * L'abonnement de cet appareil est-il enregistré en base ? C'est la moitié de
 * l'état honnête de l'interrupteur « Push actives » : le navigateur peut garder un
 * abonnement que le serveur a supprimé (service push qui répond 410) ou n'a jamais
 * reçu (enregistrement raté) — l'interrupteur était alors vert sans qu'aucun push
 * ne puisse partir.
 */
export const hasPushSubscriptionRow = async (endpoint: string): Promise<boolean> => {
  const userId = await requireUserId()
  const { count, error } = await supabase
    .from("push_subscriptions")
    .select("endpoint", { count: "exact", head: true })
    .eq("user_id", userId)
    .eq("endpoint", endpoint)
  if (error) throw new Error(`Lecture souscription push impossible : ${error.message}`)
  return (count ?? 0) > 0
}

export const deletePushSubscription = async (endpoint: string): Promise<void> => {
  const userId = await requireUserId()
  const { error } = await supabase
    .from("push_subscriptions")
    .delete()
    .eq("user_id", userId)
    .eq("endpoint", endpoint)
  if (error) throw new Error(`Suppression souscription push échouée : ${error.message}`)
}

export const getPushPreferences = async (): Promise<NotificationPreferencesRow | null> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("notification_preferences")
    .select("*")
    .eq("user_id", userId)
    .maybeSingle()
  if (error) { console.error("[getPushPreferences] error:", error); return null }
  return data as NotificationPreferencesRow | null
}

export const updatePushPreferences = async (prefs: NotificationPreferencesInput): Promise<NotificationPreferencesRow> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("notification_preferences")
    .upsert([{ user_id: userId, ...prefs, updated_at: new Date().toISOString() }], { onConflict: "user_id" })
    .select().single()
  if (error) throw new Error(`Mise à jour préférences push échouée : ${error.message}`)
  return data as NotificationPreferencesRow
}

// ============================================
// ONBOARDING (Session 8)
// ============================================

export type TipKey =
  | "tip_welcome_seen"
  | "tip_post_hooper_seen"
  | "tip_push_j3_seen"
  | "tip_calendar_move_seen"

export type OnboardingStateRow = {
  user_id: string
  tip_welcome_seen: boolean
  tip_post_session_seen: boolean
  tip_post_hooper_seen: boolean
  tip_push_j3_seen: boolean
  /** Astuce « Déplacer » du calendrier (lot 3) — comptes antérieurs marqués « vue ». */
  tip_calendar_move_seen: boolean
  created_at: string
  updated_at: string
}

export const getOnboardingState = async (): Promise<OnboardingStateRow | null> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("user_onboarding_state").select("*").eq("user_id", userId).maybeSingle()
  if (error) { console.error("[getOnboardingState] error:", error); return null }
  return data as OnboardingStateRow | null
}

export const markOnboardingTipSeen = async (key: TipKey): Promise<void> => {
  const userId = await requireUserId()
  const { error } = await supabase
    .from("user_onboarding_state")
    .update({ [key]: true, updated_at: new Date().toISOString() })
    .eq("user_id", userId)
  if (error) throw new Error(`markOnboardingTipSeen échouée : ${error.message}`)
}

export const getHasTrainingLoad = async (): Promise<boolean> => {
  const { count, error } = await supabase
    .from("training_load").select("*", { count: "exact", head: true }).limit(1)
  if (error) return false
  return (count ?? 0) >= 1
}

export const getHasDailyWellness = async (): Promise<boolean> => {
  const { count, error } = await supabase
    .from("daily_wellness").select("*", { count: "exact", head: true }).limit(1)
  if (error) return false
  return (count ?? 0) >= 1
}

// ============================================
// ADMIN (Session 10)
// ============================================

export type AdminUserListRow = {
  user_id: string
  first_name: string
  email: string
  signup_at: string
  goal_id: string
  level_id: string
  offer_id: string | null
  subscription_status: SubscriptionStatus | null
  trial_ends_at: string | null
  last_hooper_date: string | null
  last_hooper_score: number | null
  last_hooper_status: string | null
  last_acwr_date: string | null
  last_acwr_zone: string | null
  last_session_date: string | null
}

export type AdminFatigueBucket = "all" | "good" | "warning" | "bad"
/**
 * « trialing » désignait indistinctement les essais en cours et les essais
 * expirés — un compte dont la semaine est écoulée depuis trois mois apparaissait
 * dans le filtre « Essai ». Les deux sont désormais séparés : ce sont deux
 * populations à traiter différemment (l'une vit sa semaine, l'autre attend une
 * relance ou sera supprimée à 6 mois).
 */
export type AdminSubscriptionFilter =
  | "all" | "trialing" | "trial-expired" | "active" | "canceled" | "no-sub"

export type AdminListFilters = {
  subscription?: AdminSubscriptionFilter
  fatigue?: AdminFatigueBucket
  inactiveDays?: number
}

export type AdminUserDetail = {
  wizard: WizardSubmissionRow
  athlete_profile: AthleteProfileRow | null
  /** Blessures déclarées dans le diagnostic (vide sans accord). */
  injuries: AthleteInjuryRow[]
  subscription: SubscriptionRow | null
  daily_wellness_28d: DailyWellnessRow[]
  training_load_28d: TrainingLoadRow[]
  session_feedback_28d: SessionFeedbackRow[]
  /** Appareils abonnés aux push (l'adresse d'envoi n'est pas exposée, seulement son service). */
  push_devices: AdminPushDevice[]
  /** Abonnements coupés par le service push (410 / 404), du plus récent au plus ancien. */
  push_events: PushSubscriptionEventRow[]
}

export type AdminPushDevice = {
  /** Hôte de l'adresse d'envoi : web.push.apple.com, fcm.googleapis.com… */
  push_service: string
  user_agent: string | null
  created_at: string
}

export type PushSubscriptionEventRow = {
  id: string
  user_id: string
  event: "expired"
  push_service: string
  user_agent: string | null
  subscribed_at: string | null
  http_status: number | null
  occurred_at: string
}

function pushServiceOf(endpoint: string): string {
  try {
    return new URL(endpoint).host
  } catch {
    return "inconnu"
  }
}

const FATIGUE_GOOD: ReadonlySet<string> = new Set(["excellent", "bon"])
const FATIGUE_WARNING: ReadonlySet<string> = new Set(["moyen"])
const FATIGUE_BAD: ReadonlySet<string> = new Set(["mauvais", "très mauvais"])

export function applyAdminFilters(list: AdminUserListRow[], f?: AdminListFilters): AdminUserListRow[] {
  if (!f) return list
  let result = list
  if (f.subscription && f.subscription !== "all") {
    const trialOver = (r: AdminUserListRow) =>
      r.subscription_status === "trialing" &&
      r.trial_ends_at !== null &&
      new Date(r.trial_ends_at) <= new Date()
    if (f.subscription === "no-sub") {
      result = result.filter((r) => r.subscription_status === null)
    } else if (f.subscription === "trial-expired") {
      result = result.filter(trialOver)
    } else if (f.subscription === "trialing") {
      result = result.filter((r) => r.subscription_status === "trialing" && !trialOver(r))
    } else {
      result = result.filter((r) => r.subscription_status === f.subscription)
    }
  }
  if (f.fatigue && f.fatigue !== "all") {
    const bucket =
      f.fatigue === "good" ? FATIGUE_GOOD :
      f.fatigue === "warning" ? FATIGUE_WARNING :
      FATIGUE_BAD
    result = result.filter((r) => r.last_hooper_status !== null && bucket.has(r.last_hooper_status))
  }
  if (f.inactiveDays !== undefined && f.inactiveDays > 0) {
    const cutoff = new Date()
    cutoff.setDate(cutoff.getDate() - f.inactiveDays)
    result = result.filter((r) => !r.last_session_date || new Date(r.last_session_date) < cutoff)
  }
  return result
}

export const adminListUsers = async (filters?: AdminListFilters): Promise<AdminUserListRow[]> => {
  const [wizardRes, subsRes, hooperRes, acwrRes, sessionRes] = await Promise.all([
    supabase
      .from("wizard_submissions")
      .select("user_id, first_name, email, goal_id, level_id, offer_id, created_at")
      .order("created_at", { ascending: false }),
    supabase.from("subscriptions").select("user_id, status, trial_ends_at"),
    supabase
      .from("daily_wellness")
      .select("user_id, date, hooper_score, hooper_status")
      .order("date", { ascending: false }),
    supabase
      .from("training_load")
      .select("user_id, date, acwr_zone")
      .order("date", { ascending: false }),
    supabase
      .from("session_feedback")
      .select("user_id, completed_at")
      .order("completed_at", { ascending: false }),
  ])

  if (wizardRes.error) throw new Error(`adminListUsers (wizard) : ${wizardRes.error.message}`)

  // Indexer la dernière entrée par user_id (les requêtes sont DESC, donc première = la plus récente).
  const subs = new Map<string, { status: SubscriptionStatus; trial_ends_at: string | null }>()
  for (const r of subsRes.data ?? []) {
    subs.set(r.user_id as string, {
      status: r.status as SubscriptionStatus,
      trial_ends_at: (r.trial_ends_at as string | null) ?? null,
    })
  }

  type LatestHooper = { date: string; hooper_score: number; hooper_status: string | null }
  const hooper = new Map<string, LatestHooper>()
  for (const r of hooperRes.data ?? []) {
    const uid = r.user_id as string
    if (!hooper.has(uid)) {
      hooper.set(uid, {
        date: r.date as string,
        hooper_score: r.hooper_score as number,
        hooper_status: (r.hooper_status as string | null) ?? null,
      })
    }
  }

  type LatestAcwr = { date: string; acwr_zone: string | null }
  const acwr = new Map<string, LatestAcwr>()
  for (const r of acwrRes.data ?? []) {
    const uid = r.user_id as string
    if (!acwr.has(uid)) {
      acwr.set(uid, { date: r.date as string, acwr_zone: (r.acwr_zone as string | null) ?? null })
    }
  }

  const sess = new Map<string, { completed_at: string }>()
  for (const r of sessionRes.data ?? []) {
    const uid = r.user_id as string
    if (!sess.has(uid)) {
      sess.set(uid, { completed_at: r.completed_at as string })
    }
  }

  const list: AdminUserListRow[] = (wizardRes.data ?? []).map((w) => {
    const uid = w.user_id as string
    const s = subs.get(uid)
    const h = hooper.get(uid)
    const a = acwr.get(uid)
    const last = sess.get(uid)
    return {
      user_id: uid,
      first_name: w.first_name as string,
      email: w.email as string,
      signup_at: w.created_at as string,
      goal_id: w.goal_id as string,
      level_id: w.level_id as string,
      offer_id: (w.offer_id as string | null) ?? null,
      subscription_status: s?.status ?? null,
      trial_ends_at: s?.trial_ends_at ?? null,
      last_hooper_date: h?.date ?? null,
      last_hooper_score: h?.hooper_score ?? null,
      last_hooper_status: h?.hooper_status ?? null,
      last_acwr_date: a?.date ?? null,
      last_acwr_zone: a?.acwr_zone ?? null,
      last_session_date: last?.completed_at ?? null,
    }
  })

  return applyAdminFilters(list, filters)
}

export const adminGetUserDetail = async (userId: string): Promise<AdminUserDetail> => {
  const cutoff = new Date()
  cutoff.setDate(cutoff.getDate() - 28)
  const cutoffDate = cutoff.toISOString().slice(0, 10)
  const cutoffIso = cutoff.toISOString()

  const [wizardRes, athleteRes, injuriesRes, subRes, hooperRes, acwrRes, sessionRes, pushRes, pushEventsRes] = await Promise.all([
    supabase.from("wizard_submissions").select("*").eq("user_id", userId).maybeSingle(),
    supabase.from("athlete_profiles").select("*").eq("user_id", userId).maybeSingle(),
    supabase.from("athlete_injuries").select("*").eq("user_id", userId).order("created_at", { ascending: true }),
    supabase.from("subscriptions").select("*").eq("user_id", userId).maybeSingle(),
    supabase.from("daily_wellness").select("*").eq("user_id", userId).gte("date", cutoffDate).order("date", { ascending: true }),
    supabase.from("training_load").select("*").eq("user_id", userId).gte("date", cutoffDate).order("date", { ascending: true }),
    supabase.from("session_feedback").select("*").eq("user_id", userId).gte("completed_at", cutoffIso).order("completed_at", { ascending: true }),
    supabase.from("push_subscriptions").select("endpoint, user_agent, created_at").eq("user_id", userId).order("created_at", { ascending: true }),
    supabase.from("push_subscription_events").select("*").eq("user_id", userId).order("occurred_at", { ascending: false }).limit(5),
  ])

  if (wizardRes.error) throw new Error(`adminGetUserDetail (wizard) : ${wizardRes.error.message}`)
  if (!wizardRes.data) throw new Error(`adminGetUserDetail : user ${userId} introuvable`)

  return {
    wizard: wizardRes.data as WizardSubmissionRow,
    athlete_profile: athleteRes.data ? parseAthleteProfileRow(athleteRes.data as Record<string, unknown>) : null,
    injuries: (injuriesRes.data ?? []) as AthleteInjuryRow[],
    subscription: (subRes.data ?? null) as SubscriptionRow | null,
    daily_wellness_28d: (hooperRes.data ?? []) as DailyWellnessRow[],
    training_load_28d: (acwrRes.data ?? []) as TrainingLoadRow[],
    session_feedback_28d: (sessionRes.data ?? []) as SessionFeedbackRow[],
    push_devices: ((pushRes.data ?? []) as { endpoint: string; user_agent: string | null; created_at: string }[])
      .map((d) => ({ push_service: pushServiceOf(d.endpoint), user_agent: d.user_agent, created_at: d.created_at })),
    push_events: (pushEventsRes.data ?? []) as PushSubscriptionEventRow[],
  }
}

export const adminUpdateCoachNotes = async (userId: string, notes: string): Promise<void> => {
  const { error } = await supabase
    .from("athlete_profiles")
    .upsert(
      { user_id: userId, coach_notes: notes, updated_at: new Date().toISOString() },
      { onConflict: "user_id" }
    )
  if (error) throw new Error(`adminUpdateCoachNotes : ${error.message}`)
}

export type AdminSendPushResult = {
  success: boolean
  sent: number
  expired: number
  reason?: "no_subscription" | "delivery_failed"
}

export const adminSendPush = async (
  targetUserId: string,
  message: string
): Promise<AdminSendPushResult> => {
  const { data, error } = await supabase.functions.invoke("admin-broadcast-push", {
    body: { target_user_id: targetUserId, message },
  })
  if (error) throw new Error(`adminSendPush : ${error.message}`)
  return data as AdminSendPushResult
}

export type AdminStats = {
  users_total: number
  /** Essais EN COURS. Comptait auparavant toute ligne `trialing`, échéance passée comprise. */
  users_trial: number
  /** Essais arrivés à terme sans conversion : le vivier de l'écran de fin d'essai. */
  trials_expired: number
  users_active: number
  churn_30d: number
  activation_rate_7d: number
  activation_users_7d: number
  activation_total: number
  push_sent_7d: number
  push_error_rate_7d: number
}

export const adminGetStats = async (): Promise<AdminStats> => {
  const { data, error } = await supabase.rpc("admin_get_stats")
  if (error) throw new Error(`adminGetStats : ${error.message}`)
  return data as AdminStats
}


// ============================================
// PROFIL — LOT 1 (formule, photos, avis, export)
// ============================================

/** Catalogue des prix Stripe : le nom et le montant de la formule ne sont jamais en dur. */
export type StripePriceRow = {
  price_id: string
  product_name: string
  amount_cents: number
  currency: string
  interval: "month" | "year" | "one_time"
  change_allowed: boolean
  active: boolean
  /** Formule que ce prix ouvre (lot 1). NULL pour un prix hors catalogue d'offres. */
  plan: SubscriptionPlan | null
  created_at: string
}

/**
 * Le catalogue des prix actifs, par formule.
 *
 * L'écran de fin d'essai s'en sert pour décider, offre par offre, s'il propose
 * un paiement ou un appel découverte : une formule sans prix actif n'est pas
 * payable. C'est ce qui permet d'ajouter Coaching en base (lot 6) sans toucher
 * une ligne de code ici.
 */
export const listActivePrices = async (): Promise<StripePriceRow[]> => {
  const { data, error } = await supabase
    .from("stripe_prices").select("*").eq("active", true)
    .order("created_at", { ascending: true })
  if (error) { console.error("[listActivePrices] error:", error); return [] }
  return (data ?? []) as StripePriceRow[]
}

export const getStripePrice = async (priceId: string | null): Promise<StripePriceRow | null> => {
  if (!priceId) return null
  const { data, error } = await supabase
    .from("stripe_prices").select("*").eq("price_id", priceId).maybeSingle()
  if (error) { console.error("[getStripePrice] error:", error); return null }
  return data as StripePriceRow | null
}

// ── Photos d'évolution ──
export type ProgressPhotoRow = {
  id: string
  user_id: string
  storage_path: string
  taken_on: string
  program_week: number | null
  slot_week: number | null
  width: number | null
  height: number | null
  bytes: number | null
  created_at: string
}

export const PROGRESS_PHOTOS_BUCKET = "progress-photos"

/** Durée de vie des URL signées. Courte volontairement : une URL qui fuite expire vite. */
export const PROGRESS_PHOTO_SIGNED_URL_TTL_SECONDS = 300

export const listProgressPhotos = async (): Promise<ProgressPhotoRow[]> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("progress_photos").select("*")
    .eq("user_id", userId)
    .order("taken_on", { ascending: true })
  if (error) { console.error("[listProgressPhotos] error:", error); return [] }
  return (data ?? []) as ProgressPhotoRow[]
}

/**
 * Envoie une image DÉJÀ normalisée (orientation redressée, recompressée) dans le bucket
 * privé, puis enregistre la ligne. Le chemin est imposé par la policy storage :
 * <user_id>/<horodatage>.<ext>. Si l'insert échoue, l'objet est retiré — sans quoi le
 * bucket accumulerait des fichiers que plus rien ne référence.
 */
export const uploadProgressPhoto = async (
  blob: Blob,
  meta: { takenOn: string; programWeek: number | null; slotWeek: number | null; width: number; height: number }
): Promise<ProgressPhotoRow> => {
  const userId = await requireUserId()
  const ext = blob.type === "image/webp" ? "webp" : "jpg"
  const path = `${userId}/${Date.now()}.${ext}`

  const { error: upErr } = await supabase.storage
    .from(PROGRESS_PHOTOS_BUCKET)
    .upload(path, blob, { contentType: blob.type, upsert: false })
  if (upErr) throw new Error(`Envoi de la photo échoué : ${upErr.message}`)

  const { data, error } = await supabase
    .from("progress_photos")
    .insert([{
      user_id: userId,
      storage_path: path,
      taken_on: meta.takenOn,
      program_week: meta.programWeek,
      slot_week: meta.slotWeek,
      width: meta.width,
      height: meta.height,
      bytes: blob.size,
    }])
    .select().single()

  if (error) {
    await supabase.storage.from(PROGRESS_PHOTOS_BUCKET).remove([path])
    throw new Error(`Enregistrement de la photo échoué : ${error.message}`)
  }
  return data as ProgressPhotoRow
}

/** Supprime le fichier PUIS la ligne : l'inverse laisserait un objet orphelin non listé. */
export const deleteProgressPhoto = async (photo: ProgressPhotoRow): Promise<void> => {
  const { error: rmErr } = await supabase.storage
    .from(PROGRESS_PHOTOS_BUCKET).remove([photo.storage_path])
  if (rmErr) throw new Error(`Suppression du fichier échouée : ${rmErr.message}`)
  const { error } = await supabase.from("progress_photos").delete().eq("id", photo.id)
  if (error) throw new Error(`Suppression de la photo échouée : ${error.message}`)
}

/** URL signées à durée courte, en un seul aller-retour. Une entrée manquante = null. */
export const signProgressPhotos = async (paths: string[]): Promise<Record<string, string>> => {
  if (paths.length === 0) return {}
  const { data, error } = await supabase.storage
    .from(PROGRESS_PHOTOS_BUCKET)
    .createSignedUrls(paths, PROGRESS_PHOTO_SIGNED_URL_TTL_SECONDS)
  if (error) { console.error("[signProgressPhotos] error:", error); return {} }
  const map: Record<string, string> = {}
  for (const entry of data ?? []) {
    if (entry.signedUrl && entry.path) map[entry.path] = entry.signedUrl
  }
  return map
}

// ── Avis client ──
export type AppReviewInput = {
  rating: number
  comment?: string | null
  publish_consent?: boolean
  display_name?: string | null
  source?: string
}

export const submitAppReview = async (input: AppReviewInput): Promise<void> => {
  const userId = await requireUserId()
  const { error } = await supabase.from("app_reviews").insert([{
    user_id: userId,
    rating: input.rating,
    comment: input.comment ?? null,
    publish_consent: input.publish_consent ?? false,
    display_name: input.display_name ?? null,
    source: input.source ?? "settings",
  }])
  if (error) throw new Error(`Envoi de l'avis échoué : ${error.message}`)
}

/**
 * Export RGPD : tout ce que le client peut lire de lui-même, sous RLS.
 * Volontairement construit à partir des tables plutôt que d'une fonction serveur —
 * ce que l'export contient est exactement ce que ses propres policies l'autorisent à lire.
 * Les photos ne sont pas embarquées (binaire) : seuls leurs métadonnées le sont.
 *
 * Règle qui gouverne cette liste — à respecter pour tout ajout futur : on exporte ce que
 * le client a FAIT ou MESURÉ (ses séances, ses relevés, ses tests, son historique), jamais
 * le contenu ÉDITORIAL du programme qu'on lui prescrit (l'enchaînement d'exercices prévu,
 * les notes de coach, les protocoles de test, les schémas de progression — tout ce qui vit
 * dans `programs`/`program_sessions`/`exercises`). Ces trois tables ne sont **jamais**
 * interrogées ici, et chaque table de la liste est lue par un simple `select("*")` filtré
 * sur `user_id`, sans jointure — un embed Supabase (`select("*, program_sessions(*)")`)
 * ferait fuiter du contenu de programme par la bande sans que la liste ci-dessous ne le
 * révèle. `session_feedback.session_meta` et `session_set_logs.exercise_name` restent
 * légitimes malgré leur origine (un nom d'exercice, des séries/reps prescrites) : ce sont
 * des COPIES figées au moment où CE client a fait CETTE séance, pas une référence vivante
 * au catalogue — elles décrivent son activité, pas le programme en général.
 */
export const exportUserData = async (): Promise<Record<string, unknown>> => {
  const userId = await requireUserId()
  const tables = [
    // wizard_submissions est lue en entier : elle porte aussi la preuve d'accord
    // blessures (injury_consent_at, injury_consent_version).
    "wizard_submissions", "athlete_profiles", "athlete_injuries", "daily_wellness", "training_load",
    "session_feedback", "calendar_events", "weight_history", "body_measurements",
    "session_set_logs", "strength_block_targets", "rm_history", "subscriptions",
    "purchases", "notification_preferences", "progress_photos", "app_reviews",
    // Appareils dont les notifications ont été coupées (service push, navigateur, dates).
    "push_subscription_events",
    // Emails de service envoyés (fin d'essai) : type, date, état — pas l'adresse.
    "email_deliveries",
  ] as const

  const out: Record<string, unknown> = {
    exported_at: new Date().toISOString(),
    user_id: userId,
  }
  for (const table of tables) {
    const { data, error } = await supabase.from(table).select("*").eq("user_id", userId)
    out[table] = error ? { error: error.message } : (data ?? [])
  }
  return out
}

/**
 * Suppression de compte. Passe par une Edge Function : effacer un utilisateur de
 * `auth.users` demande la clé service role, qui n'a rien à faire dans le navigateur.
 * Les données suivent par ON DELETE CASCADE, les fichiers par le trigger de storage.
 */
export const requestAccountDeletion = async (): Promise<void> => {
  const { error } = await supabase.functions.invoke("delete-account", { body: {} })
  if (error) throw new Error(`Suppression du compte échouée : ${error.message}`)
}

// ── Avatar ──
export const AVATARS_BUCKET = "avatars"

/**
 * Remplace l'avatar. Chemin fixe `<user_id>/avatar.jpg` avec `upsert` : un chemin
 * horodaté laisserait s'accumuler les anciens fichiers, que plus rien ne référence
 * dès que wizard_submissions.avatar_url pointe ailleurs.
 *
 * Le cache CDN de Supabase sert l'ancienne image pendant un moment sur une URL
 * inchangée — d'où le paramètre `?v=` sur l'URL renvoyée, qui change à chaque envoi.
 */
export const uploadAvatar = async (blob: Blob): Promise<string> => {
  const userId = await requireUserId()
  const path = `${userId}/avatar.jpg`
  const { error } = await supabase.storage
    .from(AVATARS_BUCKET)
    .upload(path, blob, { contentType: blob.type, upsert: true })
  if (error) throw new Error(`Envoi de l'avatar échoué : ${error.message}`)

  const { data } = supabase.storage.from(AVATARS_BUCKET).getPublicUrl(path)
  return `${data.publicUrl}?v=${Date.now()}`
}

// ── Historique des 1RM ──
export type RmHistoryPhase = "initial" | "mid" | "final" | "manual"

export type RmHistoryRow = {
  id: string
  user_id: string
  movement: StrengthMovement
  /** Toujours en kg — charge TOTALE pour la traction lestée, jamais le lest seul. */
  value: number
  bodyweight_kg: number | null
  measured_on: string
  programme_id: string | null
  block_start_date: string | null
  phase: RmHistoryPhase
  /**
   * Le client a déclaré être allé à l'échec musculaire sur la série de test.
   * null = la question n'a pas été posée (saisie manuelle, lignes antérieures au
   * parcours de test guidé) ; false = valeur probablement sous-estimée, mais comptée
   * normalement dans le radar et les cibles.
   */
  is_max_effort: boolean | null
  created_at: string
}

export const getRmHistory = async (movement: StrengthMovement): Promise<RmHistoryRow[]> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("rm_history").select("*")
    .eq("user_id", userId)
    .eq("movement", movement)
    .order("measured_on", { ascending: true })
  if (error) { console.error("[getRmHistory] error:", error); return [] }
  return (data ?? []) as RmHistoryRow[]
}

/**
 * Ajoute (ou corrige) le point du jour. Appelé à chaque saisie de 1RM, en plus de la
 * mise à jour d'`athlete_profiles` : cette table-là ne garde que la dernière valeur, elle
 * ne peut pas porter une courbe.
 *
 * L'upsert sur (user_id, movement, measured_on) fait qu'une correction le jour même
 * remplace le point au lieu d'en empiler un deuxième.
 */
export const recordRmMeasurement = async (params: {
  movement: StrengthMovement
  valueKg: number
  measuredOn: string
  bodyweightKg?: number | null
  programmeId?: string | null
  blockStartDate?: string | null
  phase?: RmHistoryPhase
  /** Omis = la question n'a pas été posée. Ne jamais passer `true` par défaut. */
  isMaxEffort?: boolean | null
}): Promise<void> => {
  const userId = await requireUserId()
  const { error } = await supabase.from("rm_history").upsert([{
    user_id: userId,
    movement: params.movement,
    value: params.valueKg,
    bodyweight_kg: params.bodyweightKg ?? null,
    measured_on: params.measuredOn,
    programme_id: params.programmeId ?? null,
    block_start_date: params.blockStartDate ?? null,
    phase: params.phase ?? "manual",
    is_max_effort: params.isMaxEffort ?? null,
  }], { onConflict: "user_id,movement,measured_on" })
  if (error) throw new Error(`Enregistrement du record échoué : ${error.message}`)
}

/**
 * Date du dernier test de force enregistré, tous mouvements confondus.
 *
 * Lue dans `rm_history` et non dans `athlete_profiles.rm_test_date` : cette colonne-là est
 * réécrite à chaque saisie, y compris la correction d'un seul mouvement, et ne dit donc pas
 * de quand datent les valeurs affichées sur le radar. L'historique, lui, est daté par mesure.
 */
export const getLastRmMeasurementDate = async (): Promise<string | null> => {
  const userId = await requireUserId()
  const { data, error } = await supabase
    .from("rm_history").select("measured_on")
    .eq("user_id", userId)
    .order("measured_on", { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) { console.error("[getLastRmMeasurementDate] error:", error); return null }
  return (data?.measured_on as string | undefined) ?? null
}
